/**
 * The political reference set drawn over the weather basemap: coastline +
 * country / région / département borders, from the basemap's own
 * `openmaptiles` vector source.
 *
 * We redraw these ourselves (rather than restyling the vendor's own boundary
 * layers, which `basemapClutterLayerIds` hides) so the look is identical on
 * any OpenFreeMap style; only the {@link BorderPalette} changes. The specs are
 * plain layer objects: the webapp renders them as react-map-gl `<Layer>`s, and
 * MapLibre Native can take them as they are. They carry no anchor — each
 * platform places them itself (GL JS `beforeId`; a layer index on Native).
 *
 * One palette per map mode ({@link REFERENCE_PALETTES}). Every palette yields
 * the same layers, in the same order, with the same filters and zoom range:
 * only paint differs, so a mode switch restyles the band in place and never
 * removes or re-adds a layer (#327 MR6b).
 *
 * Country is most prominent; régions appear from z4; départements fade in
 * z6.5 → z8 so the national view isn't a mesh of internal borders.
 */

import {HIDDEN_SOURCE_LAYERS, placeLabelAnchorLayerId} from './basemapStyle';
import type {BasemapStyleDoc} from './basemapStyle';
// TYPE-ONLY, and it must stay so: mapModes imports (a value from) the
// forecast style transform, which reads REFERENCE_PALETTES at module load.
// A value import here would close that cycle and leave it undefined there.
import type {MapMode} from './mapModes';

export type BorderPalette = {
  /** Country border + coastline — the most prominent line. */
  country: string;
  /** Région / state border. */
  region: string;
  /** Département / county border. */
  dept: string;
  /** Région line opacity. */
  regionOpacity: number;
  /** Département line opacity once faded in (z8; 0 up to z6.5). */
  deptOpacity: number;
  /** Région line width at z4 and at z8 (px, interpolated linearly). */
  regionWidth: readonly [number, number];
  /** Département line width at z4 and at z8 (px, interpolated linearly). */
  deptWidth: readonly [number, number];
  /**
   * Dash pattern of the internal borders (régions + départements), in line
   * widths; solid when absent.
   */
  internalDash?: readonly number[];
  /**
   * Zoom below which régions are not drawn; drawn at every zoom when
   * absent. Applied as an opacity step, never as the layer's `minzoom`:
   * the layers must stay identical across palettes (see above).
   */
  regionMinZoom?: number;
};

export const REFERENCE_PALETTES: Readonly<
  Record<MapMode, Readonly<BorderPalette>>
> = {
  // Dark slate on the light positron land (the /map look): solid, the
  // internal borders light and thin.
  observations: {
    country: 'hsl(213,18%,30%)',
    region: 'hsl(213,12%,44%)',
    dept: 'hsl(213,10%,56%)',
    regionOpacity: 0.7,
    deptOpacity: 0.5,
    regionWidth: [0.8, 1.2],
    deptWidth: [0.4, 0.8],
  },
  // The forecast viewer's look before #327 MR5b (its basemap restyle, which
  // mobile's transformForecastBasemapStyle still applies): darker, heavier,
  // near-opaque internal borders that hold up over a saturated data field,
  // dashed [3, 1.5] (near-solid: positron's dotted [1, 1] read as speckle
  // over colour) and régions from z4 only.
  forecast: {
    country: 'hsl(213,18%,30%)',
    region: 'hsl(213,18%,32%)',
    dept: 'hsl(213,14%,42%)',
    regionOpacity: 0.9,
    deptOpacity: 0.9,
    regionWidth: [1, 1.5],
    deptWidth: [0.5, 1],
    internalDash: [3, 1.5],
    regionMinZoom: 4,
  },
};

export type ReferenceLayerSpec = {
  id: string;
  type: 'line';
  source: string;
  'source-layer': string;
  minzoom?: number;
  filter: unknown[];
  paint: Record<string, unknown>;
};

const SOURCE = 'openmaptiles';

/**
 * The reference band's first (lowest) layer, the coastline. A layer inserted
 * before it sits under every reference line.
 */
export const REFERENCE_BAND_ANCHOR_ID = 'ref-coastline';

/**
 * Where raster data (the rain radar) goes: just under the reference band, so
 * the borders -- and the place labels above them -- read on top of it. Only
 * when the style has the band's source, i.e. when the band can be drawn at
 * all; otherwise under the place labels (undefined: the top), since maplibre
 * refuses to add a layer before one that does not exist.
 *
 * Not the place labels while the band is drawn (#333): a GL JS layer is
 * placed when it is ADDED, and the radar adds one for every new frame, long
 * after the band is in. Inserted just under the labels, every refreshed
 * frame landed above the borders.
 */
export const dataAnchorLayerId = (
  style: BasemapStyleDoc | null | undefined,
): string | undefined =>
  style?.sources?.[SOURCE]
    ? REFERENCE_BAND_ANCHOR_ID
    : placeLabelAnchorLayerId(style);

/**
 * Id of the first symbol layer the map keeps visible: the first label of
 * the basemap that is not in {@link HIDDEN_SOURCE_LAYERS} (positron: its
 * sea names, `water_name_point_label`, then the place labels). Undefined
 * (top of the stack) while there is no style, or no such label.
 *
 * Basemap labels only -- a vector `source-layer` is required. The map's
 * own symbol layers (observations' lightning, a GeoJSON layer with none)
 * are in the style when it loads and gone in the other mode, and maplibre
 * refuses a layer added before a missing one.
 */
export const visibleLabelAnchorLayerId = (
  style: BasemapStyleDoc | null | undefined,
): string | undefined =>
  (style?.layers ?? []).find((layer) => {
    const sourceLayer = layer['source-layer'];
    return (
      layer.type === 'symbol' &&
      sourceLayer !== undefined &&
      !HIDDEN_SOURCE_LAYERS.has(sourceLayer)
    );
  })?.id;

/** Where the unified map's own layers go in the basemap style. */
export type MapLayerAnchors = {
  /**
   * Raster data (the radar, forecast rasters): under the reference band,
   * `ref-coastline`, when the style can draw it; else with the line art.
   */
  data: string | undefined;
  /**
   * Line art (forecast vectors, wind particles, the domain mask) and the
   * reference band itself: under every label the map shows
   * ({@link visibleLabelAnchorLayerId}), so labels read over borders and
   * borders over data -- sea names included, which positron stacks below
   * its place labels.
   */
  lineArt: string | undefined;
};

/**
 * Both anchors of the unified map (#327 MR6b). Undefined (top of the
 * stack) while there is no style. The legacy /map screen keeps its own
 * anchors (`placeLabelAnchorLayerId`, `dataAnchorLayerId`).
 */
export const mapLayerAnchors = (
  style: BasemapStyleDoc | null | undefined,
): MapLayerAnchors => {
  const lineArt = visibleLabelAnchorLayerId(style);
  return {
    data: style?.sources?.[SOURCE] ? REFERENCE_BAND_ANCHOR_ID : lineArt,
    lineArt,
  };
};

// Every helper returns a fresh array, so no expression node is ever shared
// between layers or between calls.

// Land borders only — drop maritime limits so we never draw lines out at sea.
const land = (): unknown[] => ['!=', ['get', 'maritime'], 1];

// Boundary layer filter for a closed admin_level range, land only.
const adminRange = (min: number, max: number): unknown[] => [
  'all',
  ['>=', ['get', 'admin_level'], min],
  ['<=', ['get', 'admin_level'], max],
  land(),
];

const countryWidth = (): unknown[] => [
  'interpolate',
  ['linear'],
  ['zoom'],
  3,
  1,
  7,
  1.8,
];

// Width ramp of an internal border: [z4, z8].
const internalWidth = (width: readonly [number, number]): unknown[] => [
  'interpolate',
  ['linear'],
  ['zoom'],
  4,
  width[0],
  8,
  width[1],
];

// Absent for a solid line, so a solid palette's paint has no dash key at all.
const dash = (
  palette: Readonly<BorderPalette>,
): {'line-dasharray'?: number[]} =>
  palette.internalDash ? {'line-dasharray': [...palette.internalDash]} : {};

const regionOpacity = (palette: Readonly<BorderPalette>): unknown =>
  palette.regionMinZoom === undefined
    ? palette.regionOpacity
    : ['step', ['zoom'], 0, palette.regionMinZoom, palette.regionOpacity];

export const referenceLayerSpecs = (
  palette: Readonly<BorderPalette>,
): ReferenceLayerSpec[] => [
  // Coastline: stroke the ocean polygon edge. The basemap's water FILL sits
  // far below the data, so its coast would otherwise be hidden.
  {
    id: REFERENCE_BAND_ANCHOR_ID,
    type: 'line',
    source: SOURCE,
    'source-layer': 'water',
    filter: ['==', ['get', 'class'], 'ocean'],
    paint: {
      'line-color': palette.country,
      'line-width': countryWidth(),
    },
  },
  // Country (admin 2)
  {
    id: 'ref-border-country',
    type: 'line',
    source: SOURCE,
    'source-layer': 'boundary',
    filter: ['all', ['==', ['get', 'admin_level'], 2], land()],
    paint: {
      'line-color': palette.country,
      'line-opacity': 0.9,
      'line-width': countryWidth(),
    },
  },
  // Régions (admin 3–4)
  {
    id: 'ref-border-region',
    type: 'line',
    source: SOURCE,
    'source-layer': 'boundary',
    filter: adminRange(3, 4),
    paint: {
      'line-color': palette.region,
      'line-opacity': regionOpacity(palette),
      'line-width': internalWidth(palette.regionWidth),
      ...dash(palette),
    },
  },
  // Départements (admin 5–6) — fade in past the national view
  {
    id: 'ref-border-dept',
    type: 'line',
    source: SOURCE,
    'source-layer': 'boundary',
    minzoom: 5,
    filter: adminRange(5, 6),
    paint: {
      'line-color': palette.dept,
      'line-width': internalWidth(palette.deptWidth),
      'line-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        6.5,
        0,
        8,
        palette.deptOpacity,
      ],
      ...dash(palette),
    },
  },
];
